/**
 * Autoservicio → Saldos a favor (`/autoservicio/saldos`).
 *
 * Puerto de `Logistics::saldos_autoservicio` + `views/saldos_autoservicio/index.php`
 * del panel anterior: el sobrante de un despacho de autoservicio queda a favor del
 * cliente. Aquí se listan los **códigos de saldo** devolubles y se registra la
 * devolución en efectivo, que anula el código en el servicio y genera el egreso de
 * caja del turno elegido.
 */

import {
  CalendarPlus,
  ChevronLeft,
  ChevronRight,
  Eraser,
  HandCoins,
  Search,
} from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuthStore } from '@/features/auth/auth.store';
import { ApiError } from '@/lib/http/api-error';
import { formatCurrency, formatDateValue, formatDateTimeValue, toIsoDate } from '@/lib/utils';

import {
  useAmpliarVigencia,
  useCodigosSaldo,
  useDevolucionSaldo,
  useTurnosAutoservicio,
} from '../saldos-autoservicio.api';
import type {
  CodigoSaldoAutoservicio,
  FiltrosSaldosAutoservicio,
} from '../saldos-autoservicio.types';

/**
 * Estados del filtro.
 *
 * El servicio **desplegado** sólo acepta **un** estado por consulta: si se
 * envía una lista (`ACTIVO,EXPIRADO`) responde `M2028`. Por eso se ofrecen los
 * cuatro valores del contrato de uno en uno y no una vista combinada.
 */
const ESTADOS = [
  { valor: 'ACTIVO', etiqueta: 'Vigentes' },
  { valor: 'EXPIRADO', etiqueta: 'Vencidos' },
  { valor: 'AGOTADO', etiqueta: 'Agotados' },
  { valor: 'ANULADO', etiqueta: 'Anulados' },
];

/** Sólo se puede ampliar la vigencia de un código vigente o vencido. */
const ESTADOS_RENOVABLES = ['ACTIVO', 'EXPIRADO'];

const OPCIONES_PAGINA = [25, 50, 100, 200];

/** Días de vigencia que se sugieren al ampliar (el usuario puede cambiarlos). */
const DIAS_VIGENCIA = 30;

/**
 * Ayuda por `error.code` del servicio (MVP 746).
 *
 * Los mensajes se ramifican por el **código**, nunca por el texto: el servicio
 * puede reescribir su `message` sin que la pantalla deje de explicar qué hacer.
 */
const AYUDA_POR_CODIGO: Record<string, { texto: string; campo?: 'fecha' }> = {
  M2030: { texto: 'Elige la nueva fecha de vencimiento.', campo: 'fecha' },
  FECHA_INVALIDA: {
    texto: 'Debe ser futura y posterior a la vigencia actual del código.',
    campo: 'fecha',
  },
  CODIGO_INEXISTENTE: { texto: 'El código no existe en el sistema de saldos.' },
  CODIGO_ANULADO: { texto: 'Ya se devolvió en efectivo: no se puede renovar.' },
  CODIGO_AGOTADO: { texto: 'No le queda saldo, así que no hay nada que renovar.' },
  CODIGO_NO_DISPONIBLE: { texto: 'Tiene una operación en curso. Reintenta en unos segundos.' },
  M6022: { texto: 'El sistema de saldos no respondió. Vuelve a intentarlo.' },
  SIN_CONEXION: { texto: 'No hay conexión con el servicio de autoservicio.' },
  POS_CODE_INVALIDO: { texto: 'Revisa el POS Code configurado en la API.' },
  M2028: { texto: 'El servicio sólo acepta un estado por consulta. Elige uno de la lista.' },
  M6001: {
    texto:
      'El servicio de autoservicio no tiene configurada su API externa (EDSU_SELFSERVICE_API). Avisa al administrador.',
  },
};

/** Hoy (`AAAA-MM-DD`). */
function hoy(): string {
  return toIsoDate(new Date());
}

/** Día local (`AAAA-MM-DD`) de un instante ISO; `null` si no es válido. */
function diaLocal(valor: string): string | null {
  const fecha = new Date(valor);

  return Number.isNaN(fecha.getTime()) ? null : toIsoDate(fecha);
}

/** Suma (o resta) días a un día `AAAA-MM-DD`. */
function sumarDias(dia: string, dias: number): string {
  const [anio, mes, numero] = dia.split('-').map(Number);

  return toIsoDate(new Date(anio ?? 0, (mes ?? 1) - 1, (numero ?? 1) + dias));
}

/**
 * Fecha sugerida y fecha mínima para ampliar la vigencia de un código.
 *
 * El servicio sólo extiende hacia adelante, así que la nueva fecha tiene que ser
 * mayor que hoy **y** que la vigencia actual (de ahí que el mínimo sea el día
 * siguiente a la más lejana de las dos).
 */
function vigenciaDe(expira_at: string): { sugerida: string; minima: string } {
  const hoyDia = hoy();
  const vence = diaLocal(expira_at);
  const base = vence && vence > hoyDia ? vence : hoyDia;

  return { sugerida: sumarDias(base, DIAS_VIGENCIA), minima: sumarDias(base, 1) };
}

/** Días que faltan para el vencimiento (negativo = ya venció). */
function diasParaVencer(expira_at: string): number | null {
  const vence = diaLocal(expira_at);

  if (!vence) return null;

  const [a1, m1, d1] = vence.split('-').map(Number);
  const hoyDia = hoy();
  const [a2, m2, d2] = hoyDia.split('-').map(Number);

  return Math.round(
    (new Date(a1 ?? 0, (m1 ?? 1) - 1, d1 ?? 1).getTime() -
      new Date(a2 ?? 0, (m2 ?? 1) - 1, d2 ?? 1).getTime()) /
      86_400_000,
  );
}

/** Variante del badge según el estado del código. */
function varianteEstado(estado: string): 'success' | 'warning' | 'neutral' | 'outline' {
  switch (estado) {
    case 'ACTIVO':
      return 'success';
    case 'EXPIRADO':
      return 'warning';
    case 'AGOTADO':
      return 'outline';
    default:
      return 'neutral';
  }
}

/** Mensaje legible de un error. */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** Código de negocio del error (`''` si no lo trae). */
function codigoDe(error: unknown): string {
  return error instanceof ApiError ? error.code : '';
}

/**
 * Filtros con los que abre la pantalla.
 *
 * Sin rango de fechas: si se filtrara por alta del mes en curso, los códigos
 * vencidos de meses anteriores —los que hay que poder renovar— no aparecerían.
 * El estado por defecto es `ACTIVO`, el único formato que acepta el servicio
 * desplegado (una lista de estados responde `M2028`).
 */
const FILTROS_INICIALES: FiltrosSaldosAutoservicio = {
  estado: 'ACTIVO',
  conSaldo: true,
  desde: '',
  hasta: '',
  expiraDesde: '',
  expiraHasta: '',
  page: 1,
  pageSize: 50,
};

/**
 * Logística → Saldos Autoservicio.
 */
export function SaldosAutoservicioPage() {
  const usuario = useAuthStore((estado) => estado.user?.displayName ?? '');

  const [filtros, setFiltros] = useState<FiltrosSaldosAutoservicio>({ ...FILTROS_INICIALES });

  const listado = useCodigosSaldo(filtros);
  const devolucion = useDevolucionSaldo();
  const vigencia = useAmpliarVigencia();

  // Modal de devolución: `seleccion` null = cerrado; `codigo` vacío = manual.
  const [seleccion, setSeleccion] = useState<{ codigo: string; saldo: number } | null>(null);
  const [turnoId, setTurnoId] = useState('');
  const [operador, setOperador] = useState('');
  const [motivo, setMotivo] = useState('Devolución de sobrante al cliente');
  const [confirmar, setConfirmar] = useState(false);
  const [aviso, setAviso] = useState<{ tipo: 'success' | 'error'; titulo: string; texto: string } | null>(null);

  // Modal de ampliación de vigencia (MVP 746).
  const [renovar, setRenovar] = useState<{ codigo: string; expira_at: string } | null>(null);
  const [nuevaFecha, setNuevaFecha] = useState('');
  const [motivoVigencia, setMotivoVigencia] = useState('');
  const [operadorVigencia, setOperadorVigencia] = useState('');
  const [errorVigencia, setErrorVigencia] = useState<{ mensaje: string; codigo: string } | null>(null);

  const turnos = useTurnosAutoservicio(seleccion !== null);

  const filas = listado.data?.items ?? [];
  const total = listado.data?.total ?? 0;
  const pagina = listado.data?.page ?? filtros.page;
  const pageSize = listado.data?.pageSize ?? filtros.pageSize;
  const paginas = total > 0 ? Math.ceil(total / pageSize) : 1;

  const devolubles = filas.filter((fila) => fila.devoluble);
  const montoDevoluble = devolubles.reduce((suma, fila) => suma + fila.saldo, 0);

  const buscar = () => setFiltros((anterior) => ({ ...anterior, page: 1 }));

  const limpiar = () => setFiltros({ ...FILTROS_INICIALES });

  const abrirDevolucion = (fila?: CodigoSaldoAutoservicio) => {
    setAviso(null);
    setTurnoId('');
    setOperador('');
    setMotivo('Devolución de sobrante al cliente');
    setConfirmar(false);

    if (fila) {
      setSeleccion({ codigo: fila.codigo, saldo: fila.saldo });
    } else {
      setSeleccion({ codigo: '', saldo: 0 });
    }
  };

  const cerrar = () => {
    if (devolucion.isPending) return;
    setConfirmar(false);
    setSeleccion(null);
  };

  const confirmarDevolucion = () => {
    if (!seleccion || seleccion.codigo === '') {
      setAviso({
        tipo: 'error',
        titulo: 'No se pudo completar',
        texto: 'Debes indicar el código de saldo que se devuelve.',
      });
      return;
    }

    if (!turnoId) {
      setAviso({
        tipo: 'error',
        titulo: 'No se pudo completar',
        texto: 'Debes seleccionar el turno (caja) al que se imputa la devolución.',
      });
      return;
    }

    setConfirmar(true);
  };

  const ejecutarDevolucion = () => {
    if (!seleccion) return;

    devolucion.mutate(
      {
        codigo: seleccion.codigo,
        turnoId: Number(turnoId),
        operador: operador.trim(),
        motivo: motivo.trim(),
      },
      {
        onSuccess: (respuesta) => {
          setConfirmar(false);
          setSeleccion(null);
          setAviso({
            tipo: 'success',
            titulo: 'Devolución registrada',
            texto: `Se devolvió ${formatCurrency(respuesta.monto)} del código ${respuesta.codigo}.${
              respuesta.gasto_id ? ` Egreso de caja #${respuesta.gasto_id}.` : ' El egreso de caja quedó registrado en el turno.'
            }`,
          });
        },
        onError: (error) => {
          setConfirmar(false);
          setAviso({
            tipo: 'error',
            titulo: 'No se pudo registrar la devolución',
            texto: mensajeDe(error, 'No se pudo registrar la devolución.'),
          });
        },
      },
    );
  };

  /** Abre el diálogo de vigencia con la fecha sugerida (+30 días del máximo actual). */
  const abrirVigencia = (fila: CodigoSaldoAutoservicio) => {
    setAviso(null);
    setErrorVigencia(null);
    setRenovar({ codigo: fila.codigo, expira_at: fila.expira_at });
    setNuevaFecha(vigenciaDe(fila.expira_at).sugerida);
    setMotivoVigencia('Ampliación de vigencia solicitada por el cliente');
    setOperadorVigencia(usuario);
  };

  const cerrarVigencia = () => {
    if (vigencia.isPending) return;
    setRenovar(null);
    setErrorVigencia(null);
  };

  const ejecutarVigencia = () => {
    if (!renovar) return;

    // El código se guarda antes de mutar: el diálogo se cierra al responder.
    const codigoRenovado = renovar.codigo;

    // El servicio sólo extiende hacia adelante; se avisa antes de llamarlo en
    // lugar de esperar el viaje de ida y vuelta para saberlo.
    const minima = vigenciaDe(renovar.expira_at).minima;

    if (nuevaFecha === '' || nuevaFecha < minima) {
      setErrorVigencia({
        codigo: 'FECHA_INVALIDA',
        mensaje: `La nueva fecha debe ser ${formatDateValue(minima)} o posterior.`,
      });
      return;
    }

    vigencia.mutate(
      {
        codigo: codigoRenovado,
        fecha: nuevaFecha,
        motivo: motivoVigencia.trim(),
        operador: operadorVigencia.trim(),
      },
      {
        onSuccess: (respuesta) => {
          // El servicio responde con el código y su nuevo vencimiento; si por
          // versión no los trae, se muestra lo que sí se conoce.
          const codigo = respuesta.codigo || codigoRenovado;
          const vence = respuesta.expira_at || `${nuevaFecha}T23:59:59`;

          setRenovar(null);
          setErrorVigencia(null);
          setAviso({
            tipo: 'success',
            titulo: 'Vigencia ampliada',
            texto:
              `El código ${codigo} vence ahora el ${formatDateValue(vence)}` +
              `${respuesta.estado ? ` (${respuesta.estado.toLowerCase()})` : ''}.` +
              ' Si estaba vencido, pasó a vigente.',
          });
        },
        onError: (error) => {
          setErrorVigencia({ mensaje: mensajeDe(error, 'No se pudo ampliar la vigencia.'), codigo: codigoDe(error) });
        },
      },
    );
  };

  /** Ayuda del código de error del servicio (qué hacer y qué campo señalar). */
  const ayudaVigencia = errorVigencia ? AYUDA_POR_CODIGO[errorVigencia.codigo] : undefined;
  /** Fecha mínima aceptable para la renovación del código abierto en el diálogo. */
  const minimaVigencia = renovar ? vigenciaDe(renovar.expira_at).minima : '';

  return (
    <div className="space-y-6">
      <Alert variant="info" title="¿Qué es un saldo a favor?">
        El sobrante de un despacho de autoservicio queda a favor del cliente. Al{' '}
        <strong>devolverlo en efectivo</strong> se anula el código en el sistema de autoservicio (para que
        no se pueda volver a usar) y se registra el <strong>egreso de caja</strong> del turno
        seleccionado, de modo que el arqueo descuente el dinero entregado. Si el cliente todavía no
        lo cobra, se le puede <strong>ampliar la vigencia</strong> para que el código siga sirviendo.
      </Alert>

      {aviso ? (
        <Alert variant={aviso.tipo} title={aviso.titulo}>
          {aviso.texto}
        </Alert>
      ) : null}

      <Card>
        <CardContent className="space-y-4">
          {/* Filtros */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label htmlFor="filtro-estado" className="mb-1.5 block text-sm font-medium text-ink-700">
                Estado
              </label>
              <Select
                id="filtro-estado"
                value={filtros.estado}
                onChange={(evento) =>
                  setFiltros((anterior) => ({ ...anterior, estado: evento.target.value, page: 1 }))
                }
                className="h-10"
              >
                {ESTADOS.map((opcion) => (
                  <option key={opcion.valor} value={opcion.valor}>
                    {opcion.etiqueta}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <Input
                label="Registrado desde"
                type="date"
                value={filtros.desde}
                onChange={(evento) => setFiltros((anterior) => ({ ...anterior, desde: evento.target.value }))}
              />
            </div>

            <div>
              <Input
                label="Registrado hasta"
                type="date"
                value={filtros.hasta}
                onChange={(evento) => setFiltros((anterior) => ({ ...anterior, hasta: evento.target.value }))}
              />
            </div>

            <div>
              <Input
                label="Vence desde"
                type="date"
                hint="Para ver los próximos a vencer"
                value={filtros.expiraDesde}
                onChange={(evento) =>
                  setFiltros((anterior) => ({ ...anterior, expiraDesde: evento.target.value }))
                }
              />
            </div>

            <div>
              <Input
                label="Vence hasta"
                type="date"
                value={filtros.expiraHasta}
                onChange={(evento) =>
                  setFiltros((anterior) => ({ ...anterior, expiraHasta: evento.target.value }))
                }
              />
            </div>

            <div>
              <label htmlFor="filtro-page-size" className="mb-1.5 block text-sm font-medium text-ink-700">
                Por página
              </label>
              <Select
                id="filtro-page-size"
                value={filtros.pageSize}
                onChange={(evento) =>
                  setFiltros((anterior) => ({ ...anterior, pageSize: Number(evento.target.value), page: 1 }))
                }
                className="h-10"
              >
                {OPCIONES_PAGINA.map((valor) => (
                  <option key={valor} value={valor}>
                    {valor}
                  </option>
                ))}
              </Select>
            </div>

            <div className="flex items-center sm:pt-6">
              <label className="flex items-center gap-2 text-sm font-medium text-ink-700">
                <input
                  type="checkbox"
                  checked={filtros.conSaldo}
                  onChange={(evento) =>
                    setFiltros((anterior) => ({ ...anterior, conSaldo: evento.target.checked, page: 1 }))
                  }
                  className="size-4 rounded border-ink-300 text-brand-600 focus:ring-brand-200"
                />
                Solo con saldo
              </label>
            </div>

            <div className="flex flex-wrap items-center gap-2 sm:pt-6">
              <Button leftIcon={<Search className="size-4" aria-hidden="true" />} onClick={buscar}>
                Buscar
              </Button>
              <Button variant="ghost" leftIcon={<Eraser className="size-4" aria-hidden="true" />} onClick={limpiar}>
                Limpiar
              </Button>
            </div>
          </div>

          {/* Resumen */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-control border border-ink-100 bg-ink-50 p-3 text-center">
              <p className="text-xs text-ink-400">Códigos encontrados</p>
              <p className="text-xl font-semibold text-ink-900">{total.toLocaleString('es-PE')}</p>
            </div>
            <div className="rounded-control border border-ink-100 bg-ink-50 p-3 text-center">
              <p className="text-xs text-ink-400">Devolubles en esta página</p>
              <p className="text-xl font-semibold text-emerald-600">{devolubles.length.toLocaleString('es-PE')}</p>
            </div>
            <div className="rounded-control border border-ink-100 bg-ink-50 p-3 text-center">
              <p className="text-xs text-ink-400">Monto devolubible (página)</p>
              <p className="text-xl font-semibold text-emerald-600">{formatCurrency(montoDevoluble)}</p>
            </div>
          </div>

          {/* Tabla */}
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-ink-700">Códigos de saldo a favor</p>
            <Button
              variant="outline"
              size="sm"
              leftIcon={<HandCoins className="size-4" aria-hidden="true" />}
              onClick={() => abrirDevolucion()}
            >
              Devolver por código
            </Button>
          </div>

          {listado.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, indice) => (
                <Skeleton key={indice} className="h-12 w-full" />
              ))}
            </div>
          ) : listado.isError ? (
            <Alert variant="error" title="No se pudo cargar el listado">
              {mensajeDe(listado.error, 'Revisa la conexión con el servicio de autoservicio.')}
              {AYUDA_POR_CODIGO[codigoDe(listado.error)] ? (
                <span className="mt-1 block text-xs text-ink-600">
                  {AYUDA_POR_CODIGO[codigoDe(listado.error)]?.texto}
                </span>
              ) : null}
            </Alert>
          ) : filas.length === 0 ? (
            <Card className="p-8 text-center text-sm text-ink-500">
              No se encontraron códigos de saldo con los filtros seleccionados.
            </Card>
          ) : (
            <div className="overflow-x-auto">
              {/*
                Tabla con anchos fijos: así el contenido se recorta (con `title`)
                en lugar de empujar la tarjeta y desbordar la página. Los datos
                secundarios (reservado, alta) van como segunda línea de su celda.
              */}
              <table className="w-full min-w-[48rem] table-fixed border-collapse text-sm">
                <thead>
                  <tr className="border-b border-ink-100 text-left text-xs uppercase tracking-wider text-ink-400">
                    <th className="w-[8rem] px-3 py-2 font-semibold">Código</th>
                    <th className="px-3 py-2 font-semibold">Cliente</th>
                    <th className="w-[5rem] px-3 py-2 font-semibold">Placa</th>
                    <th className="w-[6.5rem] px-3 py-2 text-right font-semibold">Saldo</th>
                    <th className="w-[6.5rem] px-3 py-2 font-semibold">Estado</th>
                    <th className="w-[7rem] px-3 py-2 font-semibold">Expira</th>
                    <th className="w-[16rem] px-3 py-2 text-center font-semibold">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filas.map((fila) => {
                    const dias = diasParaVencer(fila.expira_at);
                    // Un código agotado o anulado ya no se puede renovar (el
                    // servicio responde 409), así que no se ofrece la acción.
                    const renovable = ESTADOS_RENOVABLES.includes(fila.estado);

                    return (
                      <tr key={fila.codigo} className="border-b border-ink-50">
                        <td className="px-3 py-2 align-top">
                          <span className="block truncate font-medium text-ink-800" title={fila.codigo}>
                            {fila.codigo || '—'}
                          </span>
                          <span className="mt-0.5 block truncate text-[11px] text-ink-400" title="Alta del código">
                            Alta {formatDateValue(fila.created_at)}
                          </span>
                        </td>
                        <td className="px-3 py-2 align-top text-ink-600">
                          <span className="block truncate" title={fila.cliente}>
                            {fila.cliente || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2 align-top text-ink-600">
                          <span className="block truncate" title={fila.placa}>
                            {fila.placa || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right align-top">
                          <span
                            className={`block truncate font-semibold ${
                              fila.devoluble ? 'text-emerald-600' : 'text-ink-600'
                            }`}
                          >
                            {formatCurrency(fila.saldo)}
                          </span>
                          {fila.saldo_reservado > 0 ? (
                            <span className="mt-0.5 block truncate text-[11px] text-amber-600" title="Reservado por una operación en curso">
                              Reservado {formatCurrency(fila.saldo_reservado)}
                            </span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 align-top">
                          <Badge variant={varianteEstado(fila.estado)}>{fila.estado || 'SIN ESTADO'}</Badge>
                        </td>
                        <td className="px-3 py-2 align-top text-ink-500">
                          <span className="block truncate">{formatDateValue(fila.expira_at)}</span>
                          {dias !== null ? (
                            <span
                              className={`mt-0.5 block text-[11px] ${
                                dias < 0 ? 'text-rose-600' : dias <= 7 ? 'text-amber-600' : 'text-ink-400'
                              }`}
                            >
                              {dias < 0
                                ? `venció hace ${Math.abs(dias)} día(s)`
                                : dias === 0
                                  ? 'vence hoy'
                                  : `en ${dias} día(s)`}
                            </span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 text-center align-top">
                          <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                            {fila.devoluble ? (
                              <Button variant="outline" size="sm" onClick={() => abrirDevolucion(fila)}>
                                Devolver
                              </Button>
                            ) : null}
                            {renovable ? (
                              <Button
                                variant="outline"
                                size="sm"
                                title="Ampliar la fecha de vencimiento del código"
                                leftIcon={<CalendarPlus className="size-3.5" aria-hidden="true" />}
                                onClick={() => abrirVigencia(fila)}
                              >
                                Ampliar vigencia
                              </Button>
                            ) : null}
                            {!fila.devoluble && !renovable ? (
                              <span className="text-xs text-ink-400">Sin acciones</span>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Paginación */}
          {!listado.isLoading && total > 0 ? (
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-ink-400">
                Página {pagina} de {paginas} · {total.toLocaleString('es-PE')} códigos
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<ChevronLeft className="size-4" aria-hidden="true" />}
                  disabled={pagina <= 1}
                  onClick={() => setFiltros((anterior) => ({ ...anterior, page: Math.max(1, pagina - 1) }))}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  rightIcon={<ChevronRight className="size-4" aria-hidden="true" />}
                  disabled={pagina >= paginas}
                  onClick={() => setFiltros((anterior) => ({ ...anterior, page: pagina + 1 }))}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Diálogo de ampliación de vigencia (MVP 746) */}
      <Dialog
        open={renovar !== null}
        onClose={cerrarVigencia}
        title="Ampliar la vigencia del código"
        description="El código seguirá sirviendo hasta la nueva fecha. Sólo se puede extender hacia adelante; si estaba vencido, vuelve a quedar vigente."
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={cerrarVigencia} disabled={vigencia.isPending}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={vigencia.isPending}
              onClick={ejecutarVigencia}
            >
              Ampliar vigencia
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <div className="rounded-control border border-ink-100 bg-ink-50 px-3 py-2">
            <p className="truncate font-mono text-sm font-semibold text-ink-900">{renovar?.codigo}</p>
            <p className="mt-0.5 text-xs text-ink-500">
              Vence actualmente el {formatDateValue(renovar?.expira_at ?? '')}
            </p>
          </div>

          <Input
            label="Nueva fecha de vencimiento"
            type="date"
            value={nuevaFecha}
            onChange={(evento) => setNuevaFecha(evento.target.value)}
            error={ayudaVigencia?.campo === 'fecha' ? errorVigencia?.mensaje : undefined}
            hint={
              ayudaVigencia?.campo === 'fecha'
                ? undefined
                : `Mínimo ${formatDateValue(minimaVigencia)} · sugerido: +${DIAS_VIGENCIA} días`
            }
          />

          <Input
            label="Motivo"
            value={motivoVigencia}
            onChange={(evento) => setMotivoVigencia(evento.target.value)}
            hint="Queda en la traza del sistema de saldos."
          />

          <Input
            label="Operador que autoriza"
            value={operadorVigencia}
            onChange={(evento) => setOperadorVigencia(evento.target.value)}
          />

          {errorVigencia && ayudaVigencia?.campo !== 'fecha' ? (
            <Alert variant="error" title="No se pudo ampliar la vigencia">
              {/* Para los códigos conocidos se muestra la explicación de la
                  pantalla (la misma tabla del MVP) y no la del servicio: decir
                  las dos era repetir el mismo mensaje con otras palabras. */}
              {ayudaVigencia ? ayudaVigencia.texto : errorVigencia.mensaje}
              {errorVigencia.codigo ? (
                <span className="mt-1 block text-xs text-ink-500">Código del servicio: {errorVigencia.codigo}</span>
              ) : null}
            </Alert>
          ) : null}
        </div>
      </Dialog>

      {/* Diálogo de devolución */}
      <Dialog
        open={seleccion !== null}
        onClose={cerrar}
        title="Devolución en efectivo"
        description="Anula el código en el autoservicio y registra el egreso de caja del turno."
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={cerrar} disabled={devolucion.isPending}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={devolucion.isPending}
              onClick={confirmarDevolucion}
            >
              Registrar devolución
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {seleccion ? (
            <Input
              label="Código de saldo"
              value={seleccion.codigo}
              readOnly={seleccion.codigo !== ''}
              onChange={(evento) => setSeleccion({ ...seleccion, codigo: evento.target.value })}
              hint="Código que muestra el cliente (puede teclearse si no aparece en el listado)."
            />
          ) : null}

          {seleccion && seleccion.saldo > 0 ? (
            <p className="text-sm text-ink-600">
              Saldo a devolver:{' '}
              <strong className="text-ink-900">{formatCurrency(seleccion.saldo)}</strong>
            </p>
          ) : null}

          <div>
            <label htmlFor="devolucion-turno" className="mb-1.5 block text-sm font-medium text-ink-700">
              Turno (caja)
            </label>
            <Select
              id="devolucion-turno"
              className="h-10"
              value={turnoId}
              disabled={turnos.isLoading}
              onChange={(evento) => setTurnoId(evento.target.value)}
            >
              <option value="">{turnos.isLoading ? 'Cargando turnos abiertos…' : 'Selecciona el turno'}</option>
              {(turnos.data?.items ?? []).map((turno) => (
                <option key={turno.id} value={turno.id}>
                  Turno #{turno.id}
                  {turno.operador ? ` - ${turno.operador}` : ''}
                  {turno.hora_llegada ? ` (${formatDateTimeValue(turno.hora_llegada)})` : ''}
                </option>
              ))}
            </Select>
            <p className="mt-1 text-xs text-ink-400">
              El egreso se registra en esta caja y descuenta el efectivo del turno.
            </p>
          </div>

          <Input
            label="Operador que autoriza"
            value={operador}
            onChange={(evento) => setOperador(evento.target.value)}
          />

          <Input
            label="Motivo"
            value={motivo}
            onChange={(evento) => setMotivo(evento.target.value)}
          />
        </div>
      </Dialog>

      {/* Confirmación */}
      <ConfirmDialog
        open={confirmar}
        title="¿Confirmas la devolución en efectivo?"
        description={`El código ${seleccion?.codigo ?? ''} quedará anulado y se registrará el egreso de caja del turno ${turnoId}.`}
        confirmLabel="Sí, entregué el efectivo"
        cancelLabel="Cancelar"
        tone="primary"
        loading={devolucion.isPending}
        onConfirm={ejecutarDevolucion}
        onClose={() => setConfirmar(false)}
      />
    </div>
  );
}
